import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getYouTubeVideos } from '../../store/youtubeSlice';
import { Col, Container, Row } from 'react-bootstrap';
import Styles from './Youtube.module.css';
import useNearViewport from './useNearViewport';
import { FaYoutube, FaPlay, FaClock, FaCalendarAlt, FaExternalLinkAlt, FaPlus, FaCheckCircle } from 'react-icons/fa';

/**
 * Lightweight YouTube facade — shows a high-res thumbnail + animated play button overlay.
 * Loads the iframe dynamically upon user interaction.
 */
function YouTubeFacade({ videoId, title, onSelect, isSelected }) {
    const [isIframeLoaded, setIsIframeLoaded] = useState(false);

    // Reset iframe load state when video changes
    useEffect(() => {
        setIsIframeLoaded(false);
    }, [videoId]);

    const handlePlay = useCallback(() => {
        onSelect(videoId);
        setIsIframeLoaded(true);
    }, [videoId, onSelect]);

    if (isSelected && isIframeLoaded) {
        return (
            <section className={Styles.facadeFrame}>
                <iframe
                    className={Styles.iframePlayer}
                    src={`https://www.youtube.com/embed/${videoId}?rel=0&autoplay=1`}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title={title || "YouTube Video Player"}
                    loading="lazy"
                ></iframe>
            </section>
        );
    }

    return (
        <section
            className={Styles.facadeFrame}
            onClick={handlePlay}
            role="button"
            aria-label={`تشغيل الفيديو: ${title}`}
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handlePlay(); } }}
        >
            <img
                src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
                onError={(e) => {
                    // Fallback to high quality thumbnail if maxres is unavailable
                    e.target.onerror = null;
                    if (!e.target.src.endsWith('/hqdefault.jpg')) e.target.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
                }}
                alt={title || 'YouTube video thumbnail'}
                className={Styles.facadeThumbnail}
                loading="lazy" decoding="async" width={1280} height={720}
            />
            <div className={Styles.facadeGradientOverlay}></div>
            
            <div className={Styles.nowPlayingTag}>
                <span className={Styles.badgePulse}></span>
                <span>اختر فيديو للعرض السريع</span>
            </div>

            <div className={Styles.playButtonCenter}>
                <FaPlay className={Styles.playIcon} />
            </div>

            {title && (
                <div className={Styles.facadeBottomInfo}>
                    <h3 className={Styles.facadeTitle}>{title}</h3>
                </div>
            )}
        </section>
    );
}

export default function YouTube() {
    const dispatch = useDispatch();
    const [sectionRef, near] = useNearViewport();
    const { videos, loading, error } = useSelector((state) => state.youtube);

    const channelId = 'UCQX14wa3LA9sr_J8fLTf5gg';
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [visibleVideos, setVisibleVideos] = useState(6);

    useEffect(() => {
        if (near) dispatch(getYouTubeVideos(channelId));
    }, [dispatch, channelId, near]);

    useEffect(() => {
        if (videos.length > 0 && !selectedVideo) {
            setSelectedVideo(videos[0].id.videoId);
        }
    }, [videos, selectedVideo]);

    const loadMoreVideos = () => {
        setVisibleVideos((prev) => prev + 6);
    };

    const handleSelectVideo = useCallback((videoId) => {
        setSelectedVideo(videoId);
        // Scroll smoothly to facade player on mobile
        if (window.innerWidth < 768) {
            const facadeElement = document.getElementById('youtube-featured-player');
            if (facadeElement) {
                facadeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    }, []);

    const selectedVideoObject = videos.find(v => v.id?.videoId === selectedVideo) || videos[0];

    return (
        <Col ref={sectionRef} xl={12} className={Styles.youtubeSection}>
            {/* Background ambient lighting effects */}
            <div className={Styles.ambientGlow1}></div>
            <div className={Styles.ambientGlow2}></div>

            <Container fluid="lg">
                {/* Section Header */}
                <Row className="justify-content-center">
                    <Col lg={10} className={Styles.headerWrapper}>
                        <a
                            href={`https://www.youtube.com/channel/${channelId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-decoration-none"
                        >
                            <div className={Styles.channelBadge}>
                                <FaYoutube size={20} color="#ff0000" />
                                <span>قناة اليوتيوب الرسمية</span>
                                <span className={Styles.badgePulse}></span>
                            </div>
                        </a>
                        <h2 className={Styles.sectionTitle}>
                            شاهد أحدث <span className={Styles.titleHighlight}>المراجعات والشروحات</span>
                        </h2>
                        <p className={Styles.sectionSubtitle}>
                            تابع دروس ومراجعات مادة الرياضيات الثانوية العامة مجاناً بصحبة الأستاذ محمد يوسف
                        </p>
                    </Col>
                </Row>

                {/* Featured Main Video Facade Player */}
                {!loading && selectedVideo && (
                    <Row className="justify-content-center" id="youtube-featured-player">
                        <Col lg={11} xl={10} className={Styles.facadeContainer}>
                            <YouTubeFacade
                                videoId={selectedVideo}
                                title={selectedVideoObject?.snippet?.title}
                                onSelect={handleSelectVideo}
                                isSelected={true}
                            />
                        </Col>
                    </Row>
                )}

                {/* Loading Skeleton Grid */}
                {loading && (
                    <Row className={`g-4 justify-content-center ${Styles.videoGrid}`}>
                        {[1, 2, 3, 4, 5, 6].map((idx) => (
                            <Col lg={4} md={6} sm={12} key={idx}>
                                <div className={Styles.skeletonCard}>
                                    <div className={Styles.skeletonThumb}></div>
                                    <div className={Styles.skeletonBody}>
                                        <div className={Styles.skeletonLine} style={{ width: '90%' }}></div>
                                        <div className={Styles.skeletonLine} style={{ width: '60%' }}></div>
                                        <div className={Styles.skeletonLine} style={{ width: '40%', marginTop: '8px' }}></div>
                                    </div>
                                </div>
                            </Col>
                        ))}
                    </Row>
                )}

                {/* Error State */}
                {error && (
                    <Row className="justify-content-center text-center my-4">
                        <Col lg={8}>
                            <div className="p-4 rounded-4 bg-danger bg-opacity-10 border border-danger text-danger">
                                <p className="mb-0 fw-bold">عذراً، حدث خطأ أثناء تحميل الفيديوهات من يوتيوب.</p>
                            </div>
                        </Col>
                    </Row>
                )}

                {/* Video Cards Grid */}
                {!loading && !error && (
                    <Row className={`g-4 justify-content-center ${Styles.videoGrid}`}>
                        {videos.slice(0, visibleVideos).map((video) => {
                            const videoId = video.id?.videoId;
                            const isSelected = selectedVideo === videoId;
                            const durationText = formatDuration(video.duration);

                            return (
                                <Col lg={4} md={6} sm={12} key={videoId}>
                                    <article
                                        className={`${Styles.videoCard} ${isSelected ? Styles.videoCardSelected : ''}`}
                                        onClick={() => handleSelectVideo(videoId)}
                                    >
                                        <div className={Styles.cardHeader}>
                                            <img
                                                src={video.snippet.thumbnails.medium?.url || video.snippet.thumbnails.default?.url}
                                                alt={video.snippet.title}
                                                className={Styles.cardThumbnail}
                                                loading="lazy" decoding="async" width={320} height={180}
                                            />
                                            {durationText && (
                                                <span className={Styles.durationBadge}>
                                                    <FaClock size={11} /> {durationText}
                                                </span>
                                            )}
                                            {isSelected && (
                                                <span className={Styles.cardPlayingIndicator}>
                                                    <FaPlay size={10} /> جاري العرض
                                                </span>
                                            )}
                                        </div>

                                        <div className={Styles.cardBody}>
                                            <h3 className={Styles.cardTitle} title={video.snippet.title}>
                                                {video.snippet.title}
                                            </h3>

                                            <div>
                                                <div className={Styles.instructorBadge}>
                                                    <FaCheckCircle size={13} color="#d97706" />
                                                    <span>{video.snippet.channelTitle || 'أ/ محمد يوسف - أستاذ الرياضيات'}</span>
                                                </div>

                                                <div className={Styles.cardMetaRow}>
                                                    <div className={Styles.metaItem}>
                                                        <FaCalendarAlt size={12} />
                                                        <span>{new Date(video.snippet.publishedAt).toLocaleDateString('ar-EG', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                                                    </div>
                                                    <div className={Styles.metaItem} style={{ color: '#ff0000', fontWeight: 600 }}>
                                                        <span>مشاهدة الآن</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </article>
                                </Col>
                            );
                        })}
                    </Row>
                )}

                {/* Bottom Actions CTA */}
                {!loading && !error && (
                    <Row className="justify-content-center">
                        <Col lg={12}>
                            <div className={Styles.actionsWrapper}>
                                {visibleVideos < videos.length && (
                                    <button
                                        type="button"
                                        className={Styles.loadMoreBtn}
                                        onClick={loadMoreVideos}
                                    >
                                        <FaPlus size={14} />
                                        <span>عرض المزيد من الفيديوهات</span>
                                    </button>
                                )}

                                <a
                                    href={`https://www.youtube.com/channel/${channelId}?sub_confirmation=1`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={Styles.subscribeBtn}
                                >
                                    <FaYoutube size={20} />
                                    <span>إشترك بالقناة الآن</span>
                                    <FaExternalLinkAlt size={12} style={{ opacity: 0.8 }} />
                                </a>
                            </div>
                        </Col>
                    </Row>
                )}
            </Container>
        </Col>
    );
}

function formatDuration(duration) {
    if (!duration) return null;
    const match = duration.match(/PT(\d+H)?(\d+M)?(\d+S)?/);
    if (!match) return null;

    const hours = match[1] ? match[1].slice(0, -1) : null;
    const minutes = match[2] ? match[2].slice(0, -1).padStart(2, '0') : '00';
    const seconds = match[3] ? match[3].slice(0, -1).padStart(2, '0') : '00';

    if (hours) {
        return `${hours}:${minutes}:${seconds}`;
    }
    return `${minutes}:${seconds}`;
}